import { ItemID } from "types/common";

import React from "react";
import { CloseOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Checkbox, Collapse, Drawer, Input, Radio, Select, Spin, theme, Typography } from "antd";
import { formatNumberWithK } from "helpers/formatNumberWithK";
import { useTranslations } from "translations/useTranslations";

import { filterProductsByName } from "./helpers/filterProductsByName";
import { getDefaultBrandSourceKey } from "./helpers/getDefaultBrandSourceKey";
import { MatchedBrand } from "./helpers/groupMatchedProductsByBrand";
import { getTickedProductIds } from "./helpers/matchedItemIds";

import facetsStyles from "modules/FacetsModule/FacetsModule.module.scss";
import { matchingFacetsKeys } from "modules/MatchingModule/consts/matchingFacets";
import { useMatchingUrlQueryService } from "modules/MatchingModule/helpers/useMatchingUrlQueryService";
import { useMatchingFacetsHeight } from "modules/MatchingModule/MatchingGroupsModule/hooks/useMatchingFacetsHeight";
import { useMatchingTargetsDrawerContext } from "modules/MatchingModule/MatchingListModule/components/MatchingTargetsDrawer/MatchingTargetsDrawer.context";

import { useBrandName } from "./hooks/useBrandName";
import { useMatchedBrands } from "./hooks/useMatchedBrands";
import { useMatchedProductFilter } from "./hooks/useMatchedProductFilter";
import { BRAND_SOURCES, BrandSourceKey, getBrandSource, getBrandSourceByKey } from "./consts";
import { MatchedProductDrawerProps } from "./MatchedProductDrawer.types";

import styles from "./MatchedProductDrawer.module.scss";

type BrandValue = { value: ItemID; label: React.ReactNode };

const NO_PRODUCTS: MatchedBrand["products"] = [];

/**
 * "Matched product" panel: filters the matching table by the product each row was matched to
 * (the Matching Result column), unlike the Filters drawer, whose facets read the source description.
 *
 * - Type tabs (Medical / Food / Retail) pick which products to list; only the open tab loads.
 * - Brand: brands (product lines for food) that rows in the current view are matched to, with row counts.
 * - Products: that brand's matched products. Nothing ticked means all of them.
 * The choice is kept in the URL: matched_item (product ids) goes to the matches API's /filter,
 * matched_brand only restores this panel and the chip after a reload.
 */
export const MatchedProductDrawer = React.memo<MatchedProductDrawerProps>(({ isOpen, onClose }) => {
  const translations = useTranslations();
  const { token } = theme.useToken();

  const { isDrawerOpen } = useMatchingTargetsDrawerContext();
  const height = useMatchingFacetsHeight(isDrawerOpen);

  const urlQueryService = useMatchingUrlQueryService();
  const selectedInformationKinds = urlQueryService.getValueByKey(matchingFacetsKeys.CLASS);

  const { brandId, matchedItemIds, selectBrand, setTickedProducts, clear } = useMatchedProductFilter();
  const brandName = useBrandName(brandId);

  // Tab: the picked brand's type, else the only brand-bearing Information Type in Filters, else the reviewer's pick
  const [sourceKey, setSourceKey] = React.useState<BrandSourceKey | undefined>(() =>
    getDefaultBrandSourceKey(brandId, selectedInformationKinds)
  );
  React.useEffect(() => {
    const defaultSourceKey = getDefaultBrandSourceKey(brandId, selectedInformationKinds);
    if (defaultSourceKey) setSourceKey(defaultSourceKey);
  }, [brandId, selectedInformationKinds]);

  const source = getBrandSourceByKey(sourceKey);
  const { brands, isLoading: isBrandsLoading, isError } = useMatchedBrands(source, isOpen);

  // A brand of another type stays filtered (see the chip) but isn't shown as picked on this tab
  const isBrandOnThisTab = !!brandId && getBrandSource(brandId)?.key === sourceKey;
  const selectedBrand = isBrandOnThisTab ? brands.find((brand) => brand.id === brandId) : undefined;
  const brandProducts = selectedBrand?.products || NO_PRODUCTS;
  const brandProductIds = React.useMemo(() => brandProducts.map(({ id }) => id), [brandProducts]);

  const [productQuery, setProductQuery] = React.useState("");
  const visibleProducts = React.useMemo(
    () => filterProductsByName(brandProducts, productQuery),
    [brandProducts, productQuery]
  );

  // The URL holds only what /filter needs, where nothing ticked means every matched product,
  // so the ticks are kept here and restored from the URL once per brand
  const [tickedIds, setTickedIds] = React.useState<ItemID[]>([]);
  const ticksRestoredFor = React.useRef("");
  React.useEffect(() => {
    if (!brandId) {
      ticksRestoredFor.current = "";
      setTickedIds([]);
      return;
    }
    if (!selectedBrand || ticksRestoredFor.current === brandId) return;
    ticksRestoredFor.current = brandId;
    setTickedIds(getTickedProductIds(matchedItemIds, brandProductIds));
  }, [brandId, selectedBrand, matchedItemIds, brandProductIds]);

  const brandOptions = React.useMemo(
    () =>
      brands.map(({ id, name, rowCount }) => ({
        value: id,
        title: name,
        label: (
          <div className="flex justify-between gap-2">
            <span>{name}</span>
            <Typography.Text type="secondary">{formatNumberWithK(rowCount)}</Typography.Text>
          </div>
        ),
      })),
    [brands]
  );

  const handleBrandChange = React.useCallback(
    (value?: BrandValue) => {
      setProductQuery("");
      const brand = brands.find(({ id }) => id === value?.value);
      if (brand) selectBrand(brand.id, brand.products.map(({ id }) => id));
      else clear();
    },
    [brands, selectBrand, clear]
  );

  const toggleProduct = React.useCallback(
    (productId: ItemID, checked: boolean) => {
      const nextTickedIds = checked ? [...tickedIds, productId] : tickedIds.filter((id) => id !== productId);
      setTickedIds(nextTickedIds);
      setTickedProducts(brandProductIds, nextTickedIds);
    },
    [tickedIds, brandProductIds, setTickedProducts]
  );

  const handleReset = React.useCallback(() => {
    setProductQuery("");
    clear();
  }, [clear]);

  const renderBrand = () => {
    if (!source) {
      return <Typography.Text type="secondary">{translations["matching.matched_product.no_type"]}</Typography.Text>;
    }
    if (isError) {
      return <Typography.Text type="danger">{translations["matching.matched_product.error"]}</Typography.Text>;
    }

    return (
      <Select
        showSearch
        allowClear
        labelInValue
        optionFilterProp="title"
        optionLabelProp="title"
        value={isBrandOnThisTab ? { value: brandId, label: selectedBrand?.name || brandName || brandId } : undefined}
        onChange={handleBrandChange}
        options={brandOptions}
        loading={isBrandsLoading}
        notFoundContent={
          isBrandsLoading ? <Spin size="small" /> : translations["matching.matched_product.no_brands"]
        }
        placeholder={translations[source.placeholderKey]}
        className="w-full"
      />
    );
  };

  const renderProducts = () => {
    if (!source || !isBrandOnThisTab) {
      return <Typography.Text type="secondary">{translations["matching.matched_product.no_brand"]}</Typography.Text>;
    }
    if (isBrandsLoading) return <Spin className="w-full" />;
    if (!selectedBrand) {
      return <Typography.Text type="secondary">{translations["matching.matched_product.products.empty"]}</Typography.Text>;
    }

    return (
      <>
        <Typography.Paragraph type="secondary" className="text-xs">
          {translations["matching.matched_product.products.hint"]}
        </Typography.Paragraph>
        <Input.Search
          allowClear
          placeholder={translations["matching.matched_product.products.search"]}
          value={productQuery}
          onChange={(event) => setProductQuery(event.target.value)}
          className="mb-3"
        />
        {visibleProducts.length ? (
          <div className="flex flex-col gap-2">
            {visibleProducts.map((product) => (
              <div key={product.id} className="flex items-start justify-between gap-2">
                <Checkbox
                  checked={tickedIds.includes(product.id)}
                  onChange={(event) => toggleProduct(product.id, event.target.checked)}
                >
                  {product.name}
                </Checkbox>
                <Typography.Text type="secondary" className="shrink-0">
                  {formatNumberWithK(product.rowCount)}
                </Typography.Text>
              </div>
            ))}
          </div>
        ) : (
          <Typography.Text type="secondary">{translations["matching.matched_product.products.no_match"]}</Typography.Text>
        )}
      </>
    );
  };

  return (
    <div>
      <Drawer
        title={translations["matching.matched_product.title"]}
        placement="right"
        closable={true}
        onClose={onClose}
        open={isOpen}
        getContainer={false}
        className={styles.drawer}
        mask={false}
        width={320}
        headerStyle={{ padding: "20px 12px" }}
        contentWrapperStyle={{
          position: "absolute",
          top: 0,
          ...(height && { height }),
          borderRadius: `0 ${token.borderRadius}px ${token.borderRadius}px 0`,
          overflow: "hidden",
        }}
      >
        <div className="flex flex-col w-full h-full">
          <div className="flex-grow overflow-y-auto">
            <div className="p-3">
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                value={sourceKey}
                onChange={(event) => setSourceKey(event.target.value)}
                options={BRAND_SOURCES.map(({ key }) => ({
                  value: key,
                  label: translations[`matching.matched_product.type.${key}`],
                }))}
                className="flex w-full [&>label]:flex-1 [&>label]:text-center"
              />
            </div>
            <Collapse
              expandIconPosition="end"
              defaultActiveKey={["brand", "products"]}
              expandIcon={({ isActive }) => (isActive ? <MinusOutlined /> : <PlusOutlined />)}
              className={facetsStyles.collapse}
            >
              <Collapse.Panel
                key="brand"
                header={translations[source?.titleKey || "matching.matched_product.brand"]}
              >
                {renderBrand()}
              </Collapse.Panel>
              <Collapse.Panel key="products" header={translations["matching.matched_product.products"]}>
                {renderProducts()}
              </Collapse.Panel>
            </Collapse>
          </div>
          <div
            className="sticky bottom-0 w-full py-2 border-solid border-0 border-t border-t-black/5"
            style={{ backgroundColor: token.colorBgContainer }}
          >
            <Button type="text" icon={<CloseOutlined />} onClick={handleReset} className="w-full">
              {translations["item.table.facets.reset"]}
            </Button>
          </div>
        </div>
      </Drawer>
    </div>
  );
});
